import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CompleteCard,
  Feedback,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  OptionBtn,
  OptionsGrid,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = { words: GameWord[]; onExit?: () => void };

function cleanWord(w: string) {
  return w
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\d+/g, "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();
}

const WordBuilderGame: React.FC<Props> = ({ words, onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();
  const questions = useMemo(
    () =>
      words
        .map((w) => {
          const answer = cleanWord(w.palavra);
          if (answer.length < 3 || answer.length > 10) return null;
          const tiles = shuffle(answer.split("")).map((ch, idx) => ({
            id: `${answer}-${idx}-${ch}`,
            ch,
          }));
          return { word: w, answer, tiles };
        })
        .filter(Boolean) as {
        word: GameWord;
        answer: string;
        tiles: { id: string; ch: string }[];
      }[],
    [words]
  );

  const [index, setIndex] = useState(0);
  const [built, setBuilt] = useState<string[]>([]);
  const [used, setUsed] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [checked, setChecked] = useState(false);
  const [done, setDone] = useState(false);

  const current = questions[index];
  const isCorrect = built.join("") === current?.answer;

  const pickTile = (tileIndex: number, ch: string) => {
    if (checked || used.includes(tileIndex)) return;
    setUsed((u) => [...u, tileIndex]);
    setBuilt((b) => [...b, ch]);
  };

  const handleCheck = async () => {
    if (!current || checked) return;
    setChecked(true);
    if (built.join("") === current.answer) {
      setScore((s) => s + 1);
      await addPoints("QUIZ_DATILOLOGIA");
    }
  };

  const handleNext = () => {
    if (index + 1 >= questions.length) {
      setDone(true);
      return;
    }
    setIndex((i) => i + 1);
    setBuilt([]);
    setUsed([]);
    setChecked(false);
  };

  return (
    <MobileLayout
      headerTitle="Montar Palavras"
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done || !current ? (
          <CompleteCard>
            <GameTitle>
              {done ? "Palavras montadas!" : "Sem palavras"}
            </GameTitle>
            {done ? (
              <ProgressText>
                Acertos: {score}/{questions.length}
              </ProgressText>
            ) : null}
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>
              Monte a palavra {index + 1}/{questions.length}
              {current.word.descricao
                ? ` — dica: ${current.word.descricao.slice(0, 40)}`
                : ""}
            </ProgressText>
            <GameTitle style={{ letterSpacing: 4 }}>
              {built.join("") || "—"}
            </GameTitle>
            <OptionsGrid>
              {current.tiles.map((tile, i) => (
                <OptionBtn
                  key={tile.id}
                  type="button"
                  $state={used.includes(i) ? "disabled" : "idle"}
                  disabled={used.includes(i) || checked}
                  onClick={() => pickTile(i, tile.ch)}
                >
                  {tile.ch}
                </OptionBtn>
              ))}
            </OptionsGrid>
            <Md3Button
              title="Limpar"
              variant="text"
              disabled={checked}
              onClick={() => {
                setBuilt([]);
                setUsed([]);
              }}
            />
            {!checked ? (
              <Md3Button
                title="Verificar"
                onClick={handleCheck}
                disabled={built.length === 0}
              />
            ) : (
              <>
                <Feedback $ok={isCorrect}>
                  {isCorrect ? "Correto!" : `Resposta: ${current.answer}`}
                </Feedback>
                <Md3Button title="Próxima" onClick={handleNext} />
              </>
            )}
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default WordBuilderGame;
